import React, { useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { LayoutDashboard, FilePlus2, Settings, LogOut, BookMarked, BookUser, ShieldCheck, CreditCard, Ticket } from "lucide-react";
import BrandLogo from "./BrandLogo";
import { useAuth } from "../context/AuthContext";
import SupportWidget from "./SupportWidget";
import NotificationBell from "./NotificationBell";
import GlobalNotice from "./GlobalNotice";
import BetaGate from "./BetaGate";
import { LegalFooter } from "../pages/Legal";
import TermsUpdateNotice from "./TermsUpdateNotice";
import { ViewAsBanner } from "./ViewAsSwitch";

const nav = [
  { to: "/dashboard", label: "Dashboard", short: "Home", icon: LayoutDashboard, testid: "nav-dashboard", end: true },
  { to: "/quotes/new", label: "Nuovo Preventivo", icon: FilePlus2, testid: "nav-new-quote" },
  { to: "/catalog", label: "Listino Servizi", short: "Listino", icon: BookMarked, testid: "nav-catalog" },
  { to: "/clients", label: "Rubrica Clienti", short: "Clienti", icon: BookUser, testid: "nav-clients" },
  { to: "/promo-codes", label: "Codici Promo", icon: Ticket, testid: "nav-promo-codes" },
  { to: "/billing", label: "Abbonamento", short: "Piano", icon: CreditCard, testid: "nav-billing" },
  { to: "/settings", label: "Impostazioni", short: "Opzioni", icon: Settings, testid: "nav-settings" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  useEffect(() => {
    document.body.classList.add("qf-app");
    return () => document.body.classList.remove("qf-app");
  }, []);
  const desktopItems = user?.is_super_admin
    ? [...nav, { to: "/super-admin", label: "Super Admin", short: "Admin", icon: ShieldCheck, testid: "nav-super-admin" }]
    : nav;

  return (
    <div className="min-h-screen bg-background flex">
      <aside className="hidden lg:flex w-64 flex-col border-r border-border bg-card fixed inset-y-0 left-0 z-20">
        <div className="h-16 flex items-center gap-2 px-6 border-b border-border">
          <BrandLogo />
          <div className="ml-2 flex items-center gap-1.5"><SupportWidget variant="desktop" /><NotificationBell variant="desktop" /></div>
        </div>
        <nav className="flex-1 p-4 space-y-1">
          {desktopItems.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={n.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-accent text-primary"
                    : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                }`
              }
            >
              <n.icon className="w-4.5 h-4.5" style={{ width: 18, height: 18 }} />
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-border">
          <div className="px-3 py-2 mb-2">
            <p className="text-xs text-slate-400 font-medium">Connesso come</p>
            <p className="text-sm font-semibold text-slate-800 truncate">{user?.email}</p>
          </div>
          <button
            data-testid="logout-button"
            onClick={async () => {
              await logout();
              navigate("/login");
            }}
            className="flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-sm font-medium text-slate-600 hover:bg-red-50 hover:text-red-600 transition-colors"
          >
            <LogOut style={{ width: 18, height: 18 }} />
            Esci
          </button>
        </div>
      </aside>

      <div className="flex-1 lg:ml-64 min-w-0">
        <header className="lg:hidden h-14 flex items-center gap-2 px-4 border-b border-border bg-card sticky top-0 z-10">
          <BrandLogo />
          <div className="ml-2 flex items-center gap-1.5"><SupportWidget variant="mobile" /><NotificationBell variant="mobile" /></div>
        </header>
        <main className="p-4 pb-24 sm:p-6 sm:pb-24 lg:p-10 max-w-[1400px] mx-auto">
          <ViewAsBanner /><GlobalNotice /><TermsUpdateNotice /><BetaGate />{children}
          <LegalFooter className="mt-16 pt-6 border-t border-border" /></main>
        <nav className="lg:hidden fixed bottom-0 inset-x-0 bg-card border-t border-border flex z-20">
          {desktopItems.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={`mobile-${n.testid}`}
              className={({ isActive }) =>
                `flex-1 min-w-0 flex flex-col items-center gap-0.5 py-2.5 text-[10px] sm:text-[11px] font-medium ${
                  isActive ? "text-primary" : "text-slate-500"
                }`
              }
            >
              <n.icon style={{ width: 20, height: 20 }} />
              <span className="truncate max-w-full px-0.5">{n.short || n.label.split(" ")[0]}</span>
            </NavLink>
          ))}
        </nav>
      </div>
    </div>
  );
}
